import { useEffect, useRef, useState } from 'react';
import { showApiError } from '../../services/apiError';
import { dayMonth, fromDateInput, startOfDay } from './format';
import { Translate } from './i18n';
import { PeriodKey } from './overview';

export const PERIODS: PeriodKey[] = ['today', '7d', '30d', 'custom'];

/** A purchasing list as its URL keeps it: period, status group, site, search, page and the page's own filters. */
export interface ListState {
  period: PeriodKey;
  from: string;
  to: string;
  status: string;
  /** '' = all sites. */
  site: string;
  search: string;
  page: number;
  /** Supplier, direction or reason, by URL key. */
  filters: Record<string, string>;
}

/** The list as the URL left it; the header site and the last 30 days by default. */
export const readListState = (statusKeys: string[], filterKeys: string[]): ListState => {
  const params = new URLSearchParams(location.search);
  const period = params.get('period') as PeriodKey;
  const status = params.get('status') ?? '';
  const site = params.get('site');
  return {
    period: PERIODS.includes(period) ? period : '30d',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    status: statusKeys.includes(status) ? status : 'all',
    site: site === 'all' ? '' : site || localStorage.getItem('locationId') || '',
    search: params.get('search') ?? '',
    page: Math.max(1, Number(params.get('page')) || 1),
    filters: Object.fromEntries(filterKeys.map((key) => [key, params.get(key) ?? ''])),
  };
};

export const writeListState = (state: ListState) => {
  const params = new URLSearchParams();
  params.set('period', state.period);
  if (state.period === 'custom' && state.from) params.set('from', state.from);
  if (state.period === 'custom' && state.to) params.set('to', state.to);
  if (state.status !== 'all') params.set('status', state.status);
  params.set('site', state.site || 'all');
  Object.entries(state.filters).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  if (state.search) params.set('search', state.search);
  if (state.page > 1) params.set('page', String(state.page));
  window.history.replaceState(window.history.state, '', `${location.pathname}?${params.toString()}`);
};

type Period = Pick<ListState, 'period' | 'from' | 'to'>;

/** The instants the period covers, in the browser's time zone; Custom without dates is all dates. */
export const rangeOf = (state: Period): { from?: string; to?: string } => {
  if (state.period === 'today') return { from: startOfDay(0).toISOString() };
  if (state.period === '7d') return { from: startOfDay(6).toISOString() };
  if (state.period === '30d') return { from: startOfDay(29).toISOString() };
  const from = fromDateInput(state.from);
  const to = fromDateInput(state.to);
  if (to) to.setDate(to.getDate() + 1);
  return { from: from?.toISOString(), to: to?.toISOString() };
};

/** "Last 30 days", "1 Oct – 22 Oct", "All dates". */
export const periodTextOf = (state: Period, t: Translate): string => {
  if (state.period === 'today') return t('Today');
  if (state.period === '7d') return t('Last 7 days');
  if (state.period === '30d') return t('Last 30 days');
  const from = fromDateInput(state.from);
  const to = fromDateInput(state.to);
  if (from && to) return `${dayMonth(from)} – ${dayMonth(to)}`;
  if (from) return t('From {date}', { date: dayMonth(from) });
  if (to) return t('Until {date}', { date: dayMonth(to) });
  return t('All dates');
};

/** One overview request per change: a late answer to an older request is dropped, and a new token reloads. */
export const useOverviewData = <T,>(load: () => Promise<any>, deps: unknown[], language: Record<string, string> | null) => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    setLoading(true);
    setFailed(false);
    load()
      .then((response: any) => {
        if (mine === seq.current) setData(response?.data?.data ?? null);
      })
      .catch((error: any) => {
        if (mine !== seq.current) return;
        setFailed(true);
        showApiError(error, { language });
      })
      .finally(() => {
        if (mine === seq.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadKey]);

  useEffect(() => {
    const reload = () => setReloadKey((key) => key + 1);
    document.addEventListener('newToken', reload);
    return () => document.removeEventListener('newToken', reload);
  }, []);

  return { data, loading, failed, reload: () => setReloadKey((key) => key + 1) };
};
